import { Link } from "react-router-dom";
import { BookOpenCheck, ArrowRight, Sparkles } from "lucide-react";

// Single source of truth for every reference chart we've built.
// Adding a new chart? Register it here (id + component + metadata) and add
// its topic mappings in ConceptDetail.js — that's it. The hub, standalone
// viewer, and topic pages all read from this registry.
import AminoAcidChart from "@/components/AminoAcidChart";
import NeurotransmittersChart from "@/components/NeurotransmittersChart";
import FunctionalGroupsChart from "@/components/FunctionalGroupsChart";
import EnzymeKineticsChart from "@/components/EnzymeKineticsChart";
import HormonesChart from "@/components/HormonesChart";
import MuscleTypesChart from "@/components/MuscleTypesChart";
import VitaminsChart from "@/components/VitaminsChart";
import ReagentsChart from "@/components/ReagentsChart";

export const CHART_REGISTRY = [
  {
    id: "amino-acids",
    title: "Amino Acids & Peptides",
    subject: "Biochem",
    subjectColor: "#4f7942",
    count: 20,
    groups: ["Nonpolar", "Polar", "Acidic", "Basic", "Special"],
    blurb:
      "All 20 amino acids grouped by side-chain chemistry with 1-letter/3-letter codes and R-group cheat notes.",
    component: AminoAcidChart,
    topicIds: [
      "medpath-1a-amino-acids-peptides",
      "medpath-5d-amino-acids-peptides-organic-chem-view",
    ],
  },
  {
    id: "neurotransmitters",
    title: "Neurotransmitters",
    subject: "Biology · Psych",
    subjectColor: "#c98a3b",
    count: 12,
    groups: ["Monoamines", "Small molecule", "Amino acid", "Peptides", "Gas"],
    blurb:
      "Every neurotransmitter the MCAT tests, grouped by chemistry — excitatory vs inhibitory calls out the classic distinction.",
    component: NeurotransmittersChart,
    topicIds: [
      "medpath-3a-nervous-system-structure-and-function",
      "medpath-7a-the-nervous-system-behavioral",
    ],
  },
  {
    id: "hormones",
    title: "Endocrine Hormones",
    subject: "Biology",
    subjectColor: "#c95e5e",
    count: 27,
    groups: [
      "Anterior Pituitary",
      "Posterior Pituitary",
      "Thyroid",
      "Parathyroid",
      "Adrenal Cortex",
      "Adrenal Medulla",
      "Pancreas",
      "Gonads",
      "Other",
    ],
    blurb:
      "The whole endocrine map — 27 hormones grouped by source gland, with chemistry class (peptide/steroid/amine) and antagonistic-pair mnemonics.",
    component: HormonesChart,
    topicIds: [
      "medpath-3a-endocrine-system",
      "medpath-3a-mechanisms-of-hormone-action",
      "medpath-7a-the-endocrine-system-behavioral",
      "medpath-1d-hormonal-regulation-integration-of-metabolism",
    ],
  },
  {
    id: "muscle-types",
    title: "Muscle Types Comparison",
    subject: "Biology",
    subjectColor: "#a5754a",
    count: 24,
    groups: ["Skeletal", "Cardiac", "Smooth"],
    blurb:
      "Skeletal vs cardiac vs smooth, trait-by-trait: striations, nuclei, control, Ca²⁺ source, gap junctions, fatigue.",
    component: MuscleTypesChart,
    topicIds: [
      "medpath-3b-muscle-system-function",
      "medpath-3b-muscle-structure-control-of-contraction",
      "medpath-3b-nervous-control-of-muscle",
      "medpath-3b-muscle-cell-specialized",
    ],
  },
  {
    id: "vitamins-cofactors",
    title: "Vitamins & Cofactors",
    subject: "Biochem",
    subjectColor: "#4f89b8",
    count: 18,
    groups: ["Fat-Soluble", "Water-Soluble", "Mineral Cofactor"],
    blurb:
      "Fat-soluble (ADEK) vs water-soluble (B's + C) plus mineral cofactors. Each row: vitamin → coenzyme form → deficiency disease.",
    component: VitaminsChart,
    topicIds: [
      "medpath-1a-enzyme-structure-function",
      "medpath-5d-lipids-as-signaling-molecules-and-cofactors",
      "medpath-1d-metabolism-of-fatty-acids-and-proteins",
      "medpath-3b-digestive-system",
    ],
  },
  {
    id: "functional-groups",
    title: "Functional Groups",
    subject: "Orgo",
    subjectColor: "#8b6f47",
    count: 15,
    groups: ["Alcohols", "Carbonyls", "Acids & Derivatives", "Amines", "Ethers/Others"],
    blurb:
      "The 15 functional groups you'll actually see on the MCAT, with structure hints and canonical example molecules.",
    component: FunctionalGroupsChart,
    topicIds: [
      "medpath-5d-aldehydes-and-ketones",
      "medpath-5d-alcohols",
      "medpath-5d-carboxylic-acids",
      "medpath-5d-acid-derivatives-anhydrides-amides-esters",
    ],
  },
  {
    id: "orgo-reagents",
    title: "Common Orgo Reagents",
    subject: "Orgo",
    subjectColor: "#a5754a",
    count: 27,
    groups: [
      "Oxidation",
      "Reduction",
      "Alkene Addition",
      "Base / Nucleophile",
      "Acid Derivative",
      "Organometallic",
      "Activation",
    ],
    blurb:
      "Every reagent the MCAT expects you to recognize, grouped by reaction role. Markov vs anti-Markov, syn vs anti, mild vs strong — all called out.",
    component: ReagentsChart,
    topicIds: [],
  },
  {
    id: "enzyme-kinetics",
    title: "Enzyme Kinetics",
    subject: "Biochem",
    subjectColor: "#6b8258",
    count: 12,
    groups: ["Rate", "Inhibitor type", "Regulation", "Models"],
    blurb:
      "Km, Vmax, and every inhibitor type (competitive / non-competitive / uncompetitive / mixed) with the Lineweaver-Burk shift cheatsheet.",
    component: EnzymeKineticsChart,
    topicIds: [
      "enzyme-kinetics",
      "medpath-1a-control-of-enzyme-kinetics",
      "medpath-5e-enzymes-kinetics-view",
    ],
  },
];

export function getChart(id) {
  return CHART_REGISTRY.find((c) => c.id === id);
}

export default function ChartsHub() {
  return (
    <div className="max-w-6xl mx-auto" data-testid="charts-hub">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: "var(--primary)", color: "white" }}
          >
            <BookOpenCheck size={20} />
          </div>
          <h1 className="font-display font-bold text-3xl">Reference Charts</h1>
        </div>
        <p
          className="text-sm max-w-2xl"
          style={{ color: "var(--text-secondary)" }}
        >
          Every high-yield MCAT chart in one place. Search, filter, and one-tap
          save any chart as a spaced-repetition flashcard deck. Perfect for
          weekend cram sessions or last-mile drilling.
        </p>
      </div>

      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
        data-testid="charts-hub-grid"
      >
        {CHART_REGISTRY.map((c) => (
          <Link
            key={c.id}
            to={`/app/reference/${c.id}`}
            className="pm-card p-5 hover:shadow-lg transition-shadow group"
            style={{ borderLeft: `4px solid ${c.subjectColor}` }}
            data-testid={`charts-hub-card-${c.id}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: c.subjectColor }}
                  >
                    {c.subject}
                  </span>
                  <span
                    className="text-[11px]"
                    style={{ color: "var(--text-muted)" }}
                  >
                    · {c.count} entries
                  </span>
                </div>
                <div className="font-display font-bold text-lg leading-tight">
                  {c.title}
                </div>
              </div>
              <ArrowRight
                size={18}
                className="opacity-40 group-hover:opacity-100 transition-opacity"
                style={{ color: c.subjectColor }}
              />
            </div>
            <p
              className="text-sm mb-3 leading-snug"
              style={{ color: "var(--text-secondary)" }}
            >
              {c.blurb}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {c.groups.slice(0, 5).map((g) => (
                <span
                  key={g}
                  className="text-[10px] px-2 py-0.5 rounded-full"
                  style={{
                    background: `${c.subjectColor}18`,
                    color: c.subjectColor,
                  }}
                >
                  {g}
                </span>
              ))}
              {c.groups.length > 5 && (
                <span
                  className="text-[10px] px-2 py-0.5 rounded-full"
                  style={{ color: "var(--text-muted)" }}
                >
                  +{c.groups.length - 5} more
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>

      <div
        className="mt-8 pm-card p-4 text-sm flex items-start gap-3"
        style={{ background: "var(--paper)", color: "var(--text-secondary)" }}
      >
        <Sparkles
          size={16}
          className="mt-0.5 flex-shrink-0"
          style={{ color: "var(--primary)" }}
        />
        <div>
          <b style={{ color: "var(--text)" }}>Tip:</b> Every chart page has a{" "}
          <b>Save as Flashcards</b> button that turns the whole reference into a
          spaced-repetition deck under{" "}
          <Link
            to="/app/decks"
            className="underline"
            style={{ color: "var(--primary)" }}
          >
            My Decks
          </Link>
          . Perfect for the week before test day.
        </div>
      </div>
    </div>
  );
}
